import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiArrowLeft, FiUser, FiPhone, FiCheck, FiRefreshCw,
  FiPlus, FiTrash2, FiGlobe, FiMapPin, FiShoppingBag, FiX,

} from 'react-icons/fi';
import type { PhoneNumber } from '../voice.types';
import {
  getPhoneNumbersDF,
  searchAvailableNumbers,
  purchasePhoneNumber,
  createAgentDF,
  assignAgentToNumber,
  saveTransfersDF,
  type DfAvailableNumber,
} from '../../../services/voiceDataFabricService';
import { mapDfPhone } from '../voice.data';
import VoiceSelector from './VoiceSelector';
import '../../../styles/Voice.css';

/* ── Types ── */
interface Step {
  id: number;
  label: string;
  desc: string;
}

interface TransferRule {
  id: number;
  phone: string;
  condition: string;
  type: string;
}

interface Props {
  onBack: () => void;
  onCreated?: () => void;
}

/* ── Constants ── */
const LLM_OPTIONS = [
  { value: 'gpt-4.1',           label: 'GPT 4.1 — ~$0.0517/min' },
  { value: 'gpt-4o',            label: 'GPT 4o — ~$0.0420/min' },
  { value: 'gpt-4o-mini',       label: 'GPT 4o Mini — ~$0.0180/min' },
  { value: 'claude-3-5-sonnet', label: 'Claude 3.5 Sonnet — ~$0.0600/min' },
];

const STEPS: Step[] = [
  { id: 1, label: 'Agent Details',      desc: 'Set the name and purpose of your agent.' },
  { id: 2, label: 'Voice Selection',    desc: 'Choose the voice personality.' },
  { id: 3, label: 'Phone Number',       desc: 'Assign or buy a phone number.' },
  { id: 4, label: 'Transfer Directory', desc: 'Configure transfer directory for this agent.' },
];

/* (modal removed — buy numbers is now inline in Step 3) */

/* ── AgentCreatePanel ── */
const AgentCreatePanel: React.FC<Props> = ({ onBack, onCreated }) => {
  const [activeStep, setActiveStep] = useState(1);
  const [name,             setName]             = useState('');
  const [welcomeMessage,   setWelcomeMessage]   = useState('');
  const [systemMessage,    setSystemMessage]    = useState('');
  const [voicemailMessage, setVoicemailMessage] = useState('');
  const [llm,              setLlm]              = useState('gpt-4.1');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [createdAgentId, setCreatedAgentId] = useState<string | null>(null);
  const [phones,         setPhones]         = useState<PhoneNumber[]>([]);
  const [phonesLoading,  setPhonesLoading]  = useState(false);
  const [selectedPhone,  setSelectedPhone]  = useState('');
  const [pnTab,          setPnTab]          = useState<'assign' | 'buy'>('assign');
  const [available,      setAvailable]      = useState<DfAvailableNumber[]>([]);
  const [availLoading,   setAvailLoading]   = useState(false);
  const [buyingNum,      setBuyingNum]      = useState<string | null>(null);
  const [buyError,       setBuyError]       = useState<string | null>(null);
  const [expandedBuy,    setExpandedBuy]    = useState<string | null>(null);
  const [buyLabel,       setBuyLabel]       = useState('');
  const [transferRules,  setTransferRules]  = useState<TransferRule[]>([]);
  const [nextRuleId, setNextRuleId] = useState(1);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  /* Load phone numbers on step 3 */
  useEffect(() => {
    if (activeStep !== 3) return;
    setPhonesLoading(true);
    getPhoneNumbersDF()
      .then((data) => setPhones(data.map(mapDfPhone)))
      .catch(() => {})
      .finally(() => setPhonesLoading(false));
  }, [activeStep]);

  /* Load available numbers when Buy tab selected */
  useEffect(() => {
    if (pnTab !== 'buy' || available.length > 0) return;
    setAvailLoading(true);
    setBuyError(null);
    searchAvailableNumbers()
      .then(setAvailable)
      .catch(() => setBuyError('Failed to load available numbers.'))
      .finally(() => setAvailLoading(false));
  }, [pnTab]);

  const handleBuyNumber = async (n: DfAvailableNumber, label: string) => {
    const phone = n.phone_number ?? n.phoneNumber ?? '';
    setBuyingNum(phone);
    setBuyError(null);
    try {
      await purchasePhoneNumber(phone, label.trim() || (n.friendly_name ?? n.friendlyName ?? ''));
      const mapped = (await getPhoneNumbersDF()).map(mapDfPhone);
      setPhones(mapped);
      const purchased = mapped.find((p) => p.phoneNumber === phone);
      if (purchased?.phoneNumberId) setSelectedPhone(purchased.phoneNumberId);
      setExpandedBuy(null);
      setBuyLabel('');
      setPnTab('assign');
    } catch (e: any) {
      setBuyError(e.message || 'Purchase failed.');
    } finally {
      setBuyingNum(null);
    }
  };

  /* Transfer rule helpers */
  const addTransferRule = () => {
    setTransferRules((prev) => [
      ...prev,
      { id: nextRuleId, phone: '', condition: '', type: 'conference' },
    ]);
    setNextRuleId((n) => n + 1);
  };
  const removeTransferRule = (id: number) =>
    setTransferRules((prev) => prev.filter((r) => r.id !== id));
  const updateTransferRule = (id: number, field: keyof TransferRule, value: string) =>
    setTransferRules((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  /* Validation */
  const canProceed = (): boolean => {
    if (activeStep === 1) return name.trim().length > 0;
    if (activeStep === 2) return selectedVoice.length > 0;
    return true;
  };

  /**
   * Step 2 → Next: create agent with basic fields, store returned elevenLabsAgentId.
   * Matches the portal's pattern exactly.
   */
  const handleStep2Next = async (): Promise<boolean> => {
    if (!selectedVoice) { showToast('Please select a voice.', 'error'); return false; }
    // Agent already created (user went back then forward) — skip API call
    if (createdAgentId) return true;
    setSaving(true);
    try {
      const res = await createAgentDF({
        AgentName:        name.trim(),
        WelcomeMessage:   welcomeMessage.trim(),
        SystemMessage:    systemMessage.trim(),
        VoiceSelection:   selectedVoice,
        VoiceMailMessage: voicemailMessage.trim(),
        LlmModel:         llm,
      });
     
      let agentId =
        (res as any)?.ElevenLabsAgentId ??
        (res as any)?.elevenLabsAgentId ??
        (res as any)?.agent_id ??
        (res as any)?.AgentId ??
        (res as any)?.id ??
        null;
    
      if (!agentId && typeof (res as any)?.message === 'string') {
        const match = (res as any).message.match(/ElevenLabsAgentId[:\s]+(agent_[\w]+)/i);
        if (match?.[1]) agentId = match[1];
      }
      setCreatedAgentId(agentId);
      return true;
    } catch (err: any) {
      showToast(err?.message ?? 'Failed to create agent. Please try again.', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };


  const handleFinish = async () => {
    setSaving(true);
    try {
      // Assign phone number if selected and we have an agent ID
      if (selectedPhone && createdAgentId) {
        await assignAgentToNumber(selectedPhone, createdAgentId);
      }

      // Save transfer rules if any are filled and we have an agent ID
      if (createdAgentId) {
        const validTransfers = transferRules.filter((r) => r.phone.trim());
        if (validTransfers.length > 0) {
          await saveTransfersDF(
            createdAgentId,
            validTransfers.map((r) => ({
              TransferPhoneNumber: r.phone,
              Condition: r.condition,
              TransferType: r.type,
            })),
          );
        }
      }

      showToast('Agent created successfully!');
      setTimeout(() => { onCreated?.(); onBack(); }, 1500);
    } catch (err: any) {
      showToast(err?.message ?? 'Failed to save configuration. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toastColor = toast?.type === 'success' ? '#10b981' : '#ef4444';

  return (
    <motion.div
      className='aep-root'
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.22 }}
    >
      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: `${toastColor}18`,
          border: `1px solid ${toastColor}44`,
          borderRadius: 10, padding: '10px 16px',
          color: toastColor, fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          {toast.type === 'success' ? <FiCheck size={14} /> : <FiX size={14} />}
          {toast.msg}
        </div>
      )}

      {/* Back button */}
      {/* <button className='aep-back-btn' onClick={onBack}>
        <FiArrowLeft size={14} />
        Back to Agents
      </button> */}

      {/* Stepper */}
      <div className='aep-stepper'>
        {STEPS.map((step, idx) => {
          const isActive    = step.id === activeStep;
          const isCompleted = step.id < activeStep;
          return (
            <React.Fragment key={step.id}>
              <div
                className={`aep-step${isActive ? ' aep-step--active' : ''}${isCompleted ? ' aep-step--done' : ''}`}
                onClick={() => isCompleted && setActiveStep(step.id)}
                style={{ cursor: isCompleted ? 'pointer' : 'default' }}
              >
                <div className='aep-step__circle'>
                  {isCompleted ? <FiCheck size={13} /> : step.id}
                </div>
                <div className='aep-step__meta'>
                  <span className='aep-step__label'>{step.label}</span>
                  <span className='aep-step__desc'>{step.desc}</span>
                </div>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`aep-step__line${isCompleted ? ' aep-step__line--done' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Body */}
      <div className='aep-body'>

        {/* ── Step 1: Agent Details ── */}
        {activeStep === 1 && (
          <motion.div
            key='step1'
            className='aep-section'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className='aep-section-heading'>
              <div className='aep-section-title'>Agent Details</div>
              <div className='aep-section-desc'>Set the name and purpose of your agent.</div>
            </div>

            <div className='aep-field'>
              <div className='aep-field__header'>
                <label className='aep-label'>Name Of Agent <span className='aep-required'>*</span></label>
                <span className='aep-char-count'>{name.length}/50</span>
              </div>
              <input
                className='aep-input'
                value={name}
                maxLength={50}
                onChange={(e) => setName(e.target.value)}
                placeholder='e.g. Edge Agent'
                autoFocus
              />
            </div>

            <div className='aep-field'>
              <label className='aep-label'>Welcome Message <span className='aep-required'>*</span></label>
              <input
                className='aep-input'
                value={welcomeMessage}
                onChange={(e) => setWelcomeMessage(e.target.value)}
                placeholder='e.g. Hello, {{insured_first_name}}, how can I help you today?'
              />
            </div>

            <div className='aep-field'>
              <label className='aep-label'>System Message <span className='aep-required'>*</span></label>
              <textarea
                className='aep-textarea'
                value={systemMessage}
                rows={6}
                onChange={(e) => setSystemMessage(e.target.value)}
                placeholder="Describe the agent's role and instructions..."
              />
            </div>

            <div className='aep-field'>
              <label className='aep-label'>Voicemail Message</label>
              <textarea
                className='aep-textarea'
                value={voicemailMessage}
                rows={3}
                onChange={(e) => setVoicemailMessage(e.target.value)}
                placeholder='Message to leave when voicemail is detected.'
              />
            </div>

            <div className='aep-field'>
              <label className='aep-label'>Language Model (LLM)</label>
              <select className='aep-select' value={llm} onChange={(e) => setLlm(e.target.value)}>
                {LLM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span className='aep-hint'>
                Estimated price per minute is based on your system message length. Longer prompts cost more.
              </span>
            </div>
          </motion.div>
        )}

        {/* ── Step 2: Voice Selection ── */}
        {activeStep === 2 && (
          <motion.div
            key='step2'
            className='aep-section aep-section--voices'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className='acp-voice-list-title'>Select Voice</div>
            <VoiceSelector selectedVoiceId={selectedVoice} onSelect={setSelectedVoice} />
          </motion.div>
        )}

        {/* ── Step 3: Phone Number ── */}
        {activeStep === 3 && (
          <motion.div
            key='step3'
            className='aep-section'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className='aep-section-title'>Phone Number</div>

            {/* Tabs */}
            <div className='acp-pn-tabs'>
              <button
                className={`acp-pn-tab${pnTab === 'assign' ? ' acp-pn-tab--active' : ''}`}
                onClick={() => setPnTab('assign')}
              >
                <FiPhone size={13} /> Your Numbers
              </button>
              <button
                className={`acp-pn-tab${pnTab === 'buy' ? ' acp-pn-tab--active' : ''}`}
                onClick={() => setPnTab('buy')}
              >
                <FiShoppingBag size={13} /> Buy a Number
              </button>
            </div>

            {/* Your Numbers tab */}
            {pnTab === 'assign' && (
              phonesLoading ? (
                <div className='acp-pn-grid'>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className='acp-pn-card' style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div className='skeleton skeleton-text' style={{ width: '70%', height: 14 }} />
                      <div className='skeleton skeleton-text' style={{ width: '50%', height: 10 }} />
                      <div className='skeleton skeleton-rect' style={{ width: '100%', height: 28, borderRadius: 6, marginTop: 4 }} />
                    </div>
                  ))}
                </div>
              ) :
              phones.length === 0 ? (
                <div className='acp-pn-empty'>
                  <FiPhone size={24} />
                  <span>No numbers yet.</span>
                  <button className='acp-pn-tab acp-pn-tab--active' onClick={() => setPnTab('buy')}>
                    <FiShoppingBag size={12} /> Buy a Number
                  </button>
                </div>
              ) : (
                <div className='acp-pn-grid'>
                  {phones.map((p) => (
                    <div
                      key={p.phoneNumberId}
                      className={`acp-pn-card${selectedPhone === p.phoneNumberId ? ' acp-pn-card--selected' : ''}`}
                      onClick={() => setSelectedPhone(selectedPhone === p.phoneNumberId ? '' : p.phoneNumberId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className='acp-pn-card__number'>{p.phoneNumber}</div>
                      <div className='acp-pn-card__label'>
                        <FiUser size={11} />
                        {p.assignedAgentName || p.label || p.provider || '—'}
                      </div>
                      <span className='acp-pn-assign-btn'>
                        Assign
                      </span>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* Buy a Number tab */}
            {pnTab === 'buy' && (
              availLoading ? (
                <div className='acp-pn-grid'>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className='acp-pn-card' style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div className='skeleton skeleton-text' style={{ width: '65%', height: 14 }} />
                      <div className='skeleton skeleton-text' style={{ width: '45%', height: 10 }} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <div className='skeleton skeleton-rect' style={{ width: 40, height: 10, borderRadius: 4 }} />
                        <div className='skeleton skeleton-rect' style={{ width: 50, height: 10, borderRadius: 4 }} />
                      </div>
                      <div className='skeleton skeleton-rect' style={{ width: '100%', height: 28, borderRadius: 6, marginTop: 4 }} />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {buyError && <div className='aep-save-error'>{buyError}</div>}
                  <div className='acp-pn-grid'>
                    {available.map((n, i) => {
                      const phone = n.phone_number ?? n.phoneNumber ?? '';
                      const isBuying = buyingNum === phone;
                      const isExpanded = expandedBuy === phone;
                      return (
                        <div key={i} className={`acp-pn-card${isExpanded ? ' acp-pn-card--expanded' : ''}`}>
                          <div className='acp-pn-card__number'>{n.friendly_name ?? n.friendlyName ?? phone}</div>
                          <div className='acp-pn-card__raw'>{phone}</div>
                          <div className='acp-pn-card__label'>
                            <FiGlobe size={11} /> {n.iso_country ?? 'US'}
                            <FiMapPin size={11} style={{ marginLeft: 6 }} /> {n.region ?? '—'}
                          </div>
                          {!isExpanded && (
                            <button
                              className='acp-pn-buy-btn'
                              onClick={() => { setExpandedBuy(phone); setBuyLabel(''); }}
                              disabled={!!buyingNum}
                            >
                              <FiShoppingBag size={11} /> Buy
                            </button>
                          )}
                          {isExpanded && (
                            <div className='acp-buy-expand'>
                              <label className='aep-label'>LABEL FOR THIS NUMBER</label>
                              <input
                                className='aep-input'
                                placeholder='e.g., Main Sales Line'
                                value={buyLabel}
                                onChange={(e) => setBuyLabel(e.target.value)}
                                autoFocus
                              />
                              <div className='acp-buy-expand__actions'>
                                <button
                                  className='acp-buy-cancel-btn'
                                  onClick={() => { setExpandedBuy(null); setBuyLabel(''); }}
                                  disabled={isBuying}
                                >
                                  Cancel
                                </button>
                                <button
                                  className='acp-pn-buy-btn acp-pn-buy-btn--confirm'
                                  onClick={() => handleBuyNumber(n, buyLabel)}
                                  disabled={isBuying}
                                >
                                  {isBuying
                                    ? <><FiRefreshCw size={11} className='pn-spin' /> Purchasing…</>
                                    : <><FiCheck size={11} /> Confirm Purchase</>}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )
            )}
          </motion.div>
        )}

        {/* ── Step 4: Transfer Directory ── */}
        {activeStep === 4 && (
          <motion.div
            key='step4'
            className='aep-section'
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className='acp-td-card'>
              <div className='acp-td-card__head'>
                <div>
                  <div className='acp-td-card__title'>Transfer Directory</div>
                  <div className='acp-td-card__desc'>Configure call transfer rules for your agent.</div>
                </div>
                <button className='acp-td-add-btn' onClick={addTransferRule}>
                  <FiPlus size={13} /> Add Transfer
                </button>
              </div>

              {transferRules.length === 0 ? (
                <div className='acp-td-empty'>
                  No transfer configurations added. Click "Add Transfer" to begin.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', marginTop: 'var(--space-4)' }}>
                  {transferRules.map((rule, idx) => (
                    <div key={rule.id} className='aep-tr-card'>
                      <div className='aep-tr-card__head'>
                        <span className='aep-tr-title'>Transfer Rule {idx + 1}</span>
                        {transferRules.length > 1 && (
                          <button className='aep-tr-card__del' onClick={() => removeTransferRule(rule.id)} title='Remove rule'>
                            <FiTrash2 size={13} />
                          </button>
                        )}
                      </div>

                      <div className='aep-field'>
                        <label className='aep-label'>
                          Transfer Phone Number <span className='aep-tr-badge'>E.164</span>
                        </label>
                        <input
                          className='aep-input'
                          value={rule.phone}
                          onChange={(e) => updateTransferRule(rule.id, 'phone', e.target.value)}
                          placeholder='+15136357964'
                        />
                      </div>

                      <div className='aep-field'>
                        <label className='aep-label'>Condition (Instructions)</label>
                        <textarea
                          className='aep-textarea'
                          rows={3}
                          value={rule.condition}
                          onChange={(e) => updateTransferRule(rule.id, 'condition', e.target.value)}
                          placeholder='e.g. Commercial lines are redirected here.'
                        />
                      </div>

                      <div className='aep-field aep-field--last'>
                        <label className='aep-label'>Transfer Type</label>
                        <select
                          className='aep-select'
                          value={rule.type}
                          onChange={(e) => updateTransferRule(rule.id, 'type', e.target.value)}
                        >
                          <option value='conference'>Conference</option>
                          <option value='warm'>Warm</option>
                          <option value='cold'>Cold</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ── Footer ── */}
        <div className='aep-footer'>
          <div className='aep-footer-left'>
            <button
              className='aep-footer-btn aep-footer-btn--back'
              onClick={() => {
                if (activeStep === 1) onBack();
                else setActiveStep((s) => s - 1);
              }}
              disabled={saving}
            >
              ← Back
            </button>
          </div>

          <div className='aep-footer-right'>
            {activeStep < STEPS.length ? (
              <button
                className='aep-footer-btn aep-footer-btn--next'
                onClick={async () => {
                  if (activeStep === 2) {
                    const ok = await handleStep2Next();
                    if (ok) setActiveStep((s) => s + 1);
                  } else {
                    setActiveStep((s) => Math.min(STEPS.length, s + 1));
                  }
                }}
                disabled={saving || !canProceed()}
              >
                {saving && activeStep === 2
                  ? <><FiRefreshCw size={13} className='pn-spin' /> Creating…</>
                  : 'Next →'}
              </button>
            ) : (
              <button
                className='aep-footer-btn aep-footer-btn--save'
                onClick={handleFinish}
                disabled={saving}
              >
                {saving
                  ? <><FiRefreshCw size={13} className='pn-spin' /> Saving…</>
                  : <><FiCheck size={13} /> Finish</>}
              </button>
            )}
          </div>
        </div>
      </div>


    </motion.div>
  );
};

export default AgentCreatePanel;
